export type PermissionAction = 'create' | 'read' | 'update' | 'delete' | 'export' | 'approve' | 'reject';

export type ModuleName = 
  | 'dashboard'
  | 'live-tracking'
  | 'collaboration-management'
  | 'weighbridge-points'
  | 'vehicle-inspection-forms'
  | 'vehicle-assignment-forms'
  | 'driver-incident-reports'
  | 'traffic-fines'
  | 'seyir-driver-comparison'
  | 'supply-management'
  | 'shipment-tracking'
  | 'shipment-monitoring'
  | 'driver-tracking'
  | 'documents'
  | 'warehouse-operations'
  | 'warehouse-transfer'
  | 'warehouse-ramp-assignment'
  | 'waiting-records'
  | 'customer-management'
  | 'capacity-planning'
  | 'offer-requests'
  | 'offer-portal'
  | 'vehicle-management'
  | 'vehicle-documents'
  | 'trailer-management'
  | 'vehicle-assignments'
  | 'shift-management'
  | 'shift-assignments'
  | 'personnel-management'
  | 'personnel-list'
  | 'logistics-applications'
  | 'driver-applications'
  | 'approved-drivers'
  | 'corporate-carriers'
  | 'corporate-external-vehicles'
  | 'drivers'
  | 'personnel-files'
  | 'gate-entry-exit'
  | 'onboarding-documents'
  | 'driver-leave-requests'
  | 'driver-payrolls'
  | 'base-prices'
  | 'warehouse-settings'
  | 'ramp-management'
  | 'expense-management'
  | 'expense-submission'
  | 'expense-approval'
  | 'expense-categories'
  | 'payment-orders'
  | 'cost-analysis'
  | 'accounting-workflow'
  | 'reports'
  | 'performance-reports'
  | 'fuel-analysis'
  | 'return-load-potential-report'
  | 'flexible-reports'
  | 'support-center'
  | 'help-desk-guide'
  | 'whatsapp-send'
  | 'whatsapp-web'
  | 'push-notifications'
  | 'supply-metadata'
  | 'contract-management'
  | 'api-key-management'
  | 'share-targets'
  | 'automations'
  | 'settings'
  | 'user-management'
  | 'role-management'
  | 'system-logs'
  | 'version-history'
  | 'backup-management'
  | 'integrations'
  | 'bid-system'
  | 'bid-marketplace'
  | 'my-bids'
  | 'active-jobs'
  | 'driver-tracking'
  | 'shipment-monitoring'
  | 'profile';

export interface ModulePermission {
  module: ModuleName;
  actions: PermissionAction[];
}

export interface Role {
  id: string;
  name: string;
  description: string;
  isSystemRole: boolean; // Sistem rolleri silinemez (admin, driver, vb.)
  permissions: ModulePermission[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export const MODULE_LABELS: Record<ModuleName, string> = {
  'dashboard': 'Anasayfa',
  'live-tracking': 'Canlı Takip',
  'collaboration-management': 'İş Birliği Yönetimi',
  'weighbridge-points': 'Kantar Noktalari',
  'vehicle-inspection-forms': 'Arac Kontrol Formlari',
  'vehicle-assignment-forms': 'Arac Zimmet Formlari',
  'driver-incident-reports': 'Kaza / Ariza Kayitlari',
  'traffic-fines': 'Trafik Cezalari',
  'seyir-driver-comparison': 'Seyir Karsilastirma',
  'supply-management': 'Tedarik Yönetimi',
  'shipment-tracking': 'Sevkiyat Takip',
  'shipment-monitoring': 'Sevkiyat İzleme',
  'driver-tracking': 'Sürücü Takip',
  'documents': 'Belge Yonetimi',
  'warehouse-operations': 'Depo Operasyonları',
  'warehouse-transfer': 'Depo Transferi',
  'warehouse-ramp-assignment': 'Depo Rampa Atamaları',
  'waiting-records': 'Bekleme Kayıtları',
  'customer-management': 'Müşteri Yönetimi',
  'capacity-planning': 'Kapasite Planlama',
  'offer-requests': 'Teklif Talepleri',
  'offer-portal': 'Teklif Portalı',
  'vehicle-management': 'Çekici Yönetimi',
  'vehicle-documents': 'Çekici/Dorse Belgeleri',
  'trailer-management': 'Dorse Yönetimi',
  'vehicle-assignments': 'Araç Atamaları',
  'shift-management': 'Vardiya Yönetimi',
  'shift-assignments': 'Vardiya Atamaları',
  'personnel-management': 'Personel Yönetimi',
  'personnel-list': 'Personel Listesi',
  'logistics-applications': 'Lojistik Başvuruları',
  'driver-applications': 'Spot Sürücü Başvuruları',
  'approved-drivers': 'Spot Sürücüler',
  'corporate-carriers': 'Kurumsal Nakliye Firmaları',
  'corporate-external-vehicles': 'Kurumsal Dış Nakliye Çekicileri',
  'drivers': 'Özmal Sürücülerimiz',
  'personnel-files': 'Ozlük Dosyalari',
  'gate-entry-exit': 'Nizamiye Giriş / Çıkış',
  'onboarding-documents': 'Yeni Gelen Dokümanlar',
  'driver-leave-requests': 'Sürücü İzin Talepleri',
  'driver-payrolls': 'Maaş Bordroları',
  'base-prices': 'Baz Fiyatları',
  'warehouse-settings': 'Depo Ayarları',
  'ramp-management': 'Rampa Yönetimi',
  'expense-management': 'Masraf Yönetimi',
  'expense-submission': 'Masraf Gönderimi',
  'expense-approval': 'Masraf Onayı',
  'expense-categories': 'Masraf Kategorileri',
  'payment-orders': 'Ödeme Emri',
  'cost-analysis': 'Maliyet Analizi',
  'accounting-workflow': 'Finansal İşlemler',
  'reports': 'Raporlar',
  'performance-reports': 'Performans Raporları',
  'fuel-analysis': 'Yakıt Analizi',
  'return-load-potential-report': 'Donus Yuk Potansiyeli',
  'flexible-reports': 'Ozel Rapor Olusturucu',
  'support-center': 'Destek Merkezi',
  'help-desk-guide': 'Kullanim Kilavuzu',
  'whatsapp-send': 'WhatsApp Send',
  'whatsapp-web': 'WhatsApp Web',
  'push-notifications': 'Push Bildirim',
  'supply-metadata': 'Form Alan Yönetimi',
  'contract-management': 'Sözleşme Yönetimi',
  'api-key-management': 'API Anahtarları',
  'share-targets': 'Paylaşım Hedefleri',
  'automations': 'Otomasyonlar',
  'settings': 'Sistem Ayarları',
  'user-management': 'Kullanıcı Yönetimi',
  'role-management': 'Rol Yönetimi',
  'system-logs': 'Sistem Günlükleri',
  'version-history': 'Versiyon Geçmişi',
  'backup-management': 'Yedekleme Yönetimi',
  'integrations': 'Entegrasyonlar',
  'bid-system': 'Teklif Sistemi',
  'bid-marketplace': 'Teklif Pazarı',
  'my-bids': 'Tekliflerim',
  'active-jobs': 'Aktif İşler',
  'profile': 'Profil',
};

export const PERMISSION_LABELS: Record<PermissionAction, string> = {
  'create': 'Oluşturma',
  'read': 'Görüntüleme',
  'update': 'Güncelleme',
  'delete': 'Silme',
  'export': 'Dışa Aktarma',
  'approve': 'Onaylama',
  'reject': 'Reddetme',
};

// Varsayılan sistem rolleri
export const DEFAULT_ROLES: Role[] = [
  {
    id: 'admin',
    name: 'Sistem Yöneticisi',
    description: 'Tüm yetkilere sahip sistem yöneticisi',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'live-tracking', actions: ['read'] },
      { module: 'supply-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'shipment-tracking', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'shipment-monitoring', actions: ['read', 'export'] },
      { module: 'driver-tracking', actions: ['read'] },
      { module: 'warehouse-operations', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'warehouse-transfer', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'warehouse-ramp-assignment', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'waiting-records', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'automations', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'customer-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'capacity-planning', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'collaboration-management', actions: ['read'] },
      { module: 'offer-requests', actions: ['create', 'read', 'update', 'delete', 'approve', 'reject'] },
      { module: 'offer-portal', actions: ['read'] },
      { module: 'vehicle-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'trailer-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'vehicle-assignments', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'shift-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'shift-assignments', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'personnel-management', actions: ['create', 'read', 'update', 'delete', 'export', 'approve', 'reject'] },
      { module: 'personnel-list', actions: ['read'] },
      { module: 'logistics-applications', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'driver-applications', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'approved-drivers', actions: ['read'] },
      { module: 'corporate-carriers', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'corporate-external-vehicles', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'drivers', actions: ['read'] },
      { module: 'gate-entry-exit', actions: ['create', 'read', 'update'] },
       { module: 'onboarding-documents', actions: ['create', 'read', 'update', 'delete', 'export'] },
       { module: 'driver-leave-requests', actions: ['create', 'read', 'update', 'delete', 'export'] },
       { module: 'driver-payrolls', actions: ['create', 'read', 'delete'] },
       { module: 'base-prices', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'warehouse-settings', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'ramp-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'expense-management', actions: ['create', 'read', 'update', 'delete', 'export', 'approve', 'reject'] },
      { module: 'expense-submission', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'expense-approval', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'expense-categories', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'payment-orders', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'cost-analysis', actions: ['read', 'export'] },
      { module: 'accounting-workflow', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'reports', actions: ['read', 'export'] },
      { module: 'performance-reports', actions: ['read', 'export'] },
      { module: 'fuel-analysis', actions: ['read', 'export'] },
      { module: 'support-center', actions: ['create', 'read', 'update'] },
      { module: 'whatsapp-send', actions: ['read', 'create'] },
      { module: 'whatsapp-web', actions: ['read', 'create'] },
      { module: 'push-notifications', actions: ['read', 'create'] },
      { module: 'supply-metadata', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'contract-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'api-key-management', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'share-targets', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'settings', actions: ['read', 'update'] },
      { module: 'user-management', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'role-management', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'system-logs', actions: ['read'] },
      { module: 'version-history', actions: ['read'] },
      { module: 'backup-management', actions: ['create', 'read', 'update', 'delete'] },
      { module: 'integrations', actions: ['read', 'update'] },
      { module: 'bid-system', actions: ['read'] },
      { module: 'bid-marketplace', actions: ['read'] },
      { module: 'my-bids', actions: ['read'] },
      { module: 'active-jobs', actions: ['read'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'dispatcher',
    name: 'Dispozisyon Uzmanı',
    description: 'Sevkiyat planlama ve koordinasyon yetkisi',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'live-tracking', actions: ['read'] },
      { module: 'supply-management', actions: ['read', 'update'] },
      { module: 'shipment-tracking', actions: ['create', 'read', 'update'] },
      { module: 'shipment-monitoring', actions: ['read'] },
      { module: 'driver-tracking', actions: ['read'] },
      { module: 'warehouse-operations', actions: ['read', 'update'] },
      { module: 'warehouse-transfer', actions: ['read', 'update'] },
      { module: 'warehouse-ramp-assignment', actions: ['read', 'update'] },
      { module: 'waiting-records', actions: ['read', 'update'] },
      { module: 'automations', actions: ['read', 'update'] },
      { module: 'customer-management', actions: ['read', 'update'] },
      { module: 'capacity-planning', actions: ['read', 'update'] },
      { module: 'offer-requests', actions: ['create', 'read', 'update'] },
      { module: 'offer-portal', actions: ['read'] },
      { module: 'vehicle-management', actions: ['read', 'update'] },
      { module: 'trailer-management', actions: ['create', 'read', 'update', 'delete', 'export'] },
      { module: 'vehicle-assignments', actions: ['read', 'update'] },
      { module: 'shift-management', actions: ['read', 'update'] },
      { module: 'shift-assignments', actions: ['read', 'update'] },
      { module: 'personnel-management', actions: ['read'] },
      { module: 'personnel-list', actions: ['read'] },
      { module: 'logistics-applications', actions: ['read', 'update'] },
      { module: 'driver-applications', actions: ['read', 'update'] },
      { module: 'approved-drivers', actions: ['read'] },
      { module: 'drivers', actions: ['read'] },
      { module: 'gate-entry-exit', actions: ['create', 'read', 'update'] },
      { module: 'onboarding-documents', actions: ['read', 'update'] },
      { module: 'driver-leave-requests', actions: ['read', 'update'] },
      { module: 'base-prices', actions: ['read', 'update'] },
      { module: 'warehouse-settings', actions: ['read', 'update'] },
      { module: 'ramp-management', actions: ['read', 'update'] },
      { module: 'expense-submission', actions: ['create', 'read', 'update'] },
      { module: 'expense-approval', actions: ['read', 'update'] },
      { module: 'expense-categories', actions: ['read'] },
      { module: 'payment-orders', actions: ['read'] },
      { module: 'cost-analysis', actions: ['read'] },
      { module: 'accounting-workflow', actions: ['read', 'update'] },
      { module: 'reports', actions: ['read', 'export'] },
      { module: 'performance-reports', actions: ['read', 'export'] },
      { module: 'fuel-analysis', actions: ['read', 'export'] },
      { module: 'support-center', actions: ['create', 'read'] },
      { module: 'whatsapp-send', actions: ['read', 'create'] },
      { module: 'push-notifications', actions: ['read'] },
      { module: 'settings', actions: ['read'] },
      { module: 'system-logs', actions: ['read'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'internal-driver',
    name: 'İç Sürücü',
    description: 'Şirket bünyesindeki Sürücüler',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'shipment-tracking', actions: ['read', 'update'] },
      { module: 'expense-submission', actions: ['create', 'read'] },
      { module: 'payment-orders', actions: ['read'] },
      { module: 'driver-tracking', actions: ['read', 'update'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'external-driver',
    name: 'Dış Sürücü',
    description: 'Dış kaynak Sürücüler - teklif sistemi',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'bid-system', actions: ['read', 'create', 'update'] },
      { module: 'bid-marketplace', actions: ['read', 'create'] },
      { module: 'my-bids', actions: ['read', 'update'] },
      { module: 'active-jobs', actions: ['read', 'update'] },
      { module: 'shipment-tracking', actions: ['read', 'update'] },
      { module: 'driver-tracking', actions: ['read', 'update'] },
      { module: 'expense-submission', actions: ['create', 'read'] },
      { module: 'payment-orders', actions: ['read'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'corporate-external-transport',
    name: 'Kurumsal Dış Nakliye',
    description: 'Kendi spot sürücülerini, çekicilerini ve dorselerini yönetir',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'bid-system', actions: ['read', 'create', 'update'] },
      { module: 'bid-marketplace', actions: ['read', 'create'] },
      { module: 'my-bids', actions: ['read', 'update'] },
      { module: 'active-jobs', actions: ['read'] },
      { module: 'shipment-tracking', actions: ['read'] },
      { module: 'driver-tracking', actions: ['read'] },
      { module: 'vehicle-management', actions: ['create', 'read', 'update'] },
      { module: 'trailer-management', actions: ['create', 'read', 'update'] },
      { module: 'approved-drivers', actions: ['create', 'read', 'update'] },
      { module: 'driver-applications', actions: ['create', 'read', 'update'] },
      { module: 'accounting-workflow', actions: ['read', 'update'] },
      { module: 'documents', actions: ['create', 'read', 'update'] },
      { module: 'support-center', actions: ['read', 'create'] },
      { module: 'profile', actions: ['read', 'update'] },
      { module: 'payment-orders', actions: ['create', 'read', 'update'] },
      { module: 'expense-submission', actions: ['read'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'corporate-external-transport-user',
    name: 'Kurumsal Dış Nakliye Alt Kullanıcı',
    description: 'Kurumsal dış nakliye firmasına bağlı panel kullanıcısı',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'bid-system', actions: ['read', 'create', 'update'] },
      { module: 'bid-marketplace', actions: ['read', 'create'] },
      { module: 'my-bids', actions: ['read', 'update'] },
      { module: 'active-jobs', actions: ['read'] },
      { module: 'shipment-tracking', actions: ['read'] },
      { module: 'driver-tracking', actions: ['read'] },
      { module: 'approved-drivers', actions: ['create', 'read', 'update'] },
      { module: 'vehicle-management', actions: ['create', 'read', 'update'] },
      { module: 'trailer-management', actions: ['create', 'read', 'update'] },
      { module: 'driver-applications', actions: ['create', 'read', 'update'] },
      { module: 'accounting-workflow', actions: ['read', 'update'] },
      { module: 'documents', actions: ['create', 'read', 'update'] },
      { module: 'support-center', actions: ['read', 'create'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'corporate-external-transport-driver',
    name: 'Kurumsal Dış Nakliye Sürücü',
    description: 'Kurumsal dış nakliye firmasına bağlı mobil sürücü',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'active-jobs', actions: ['read', 'update'] },
      { module: 'shipment-tracking', actions: ['read', 'update'] },
      { module: 'driver-tracking', actions: ['read', 'update'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'warehouse',
    name: 'Depo Görevlisi',
    description: 'Depo operasyonları yetkisi',
    isSystemRole: true,
    permissions: [
      { module: 'dashboard', actions: ['read'] },
      { module: 'warehouse-operations', actions: ['create', 'read', 'update'] },
      { module: 'warehouse-transfer', actions: ['create', 'read', 'update'] },
      { module: 'warehouse-ramp-assignment', actions: ['create', 'read', 'update'] },
      { module: 'waiting-records', actions: ['create', 'read'] },
      { module: 'shipment-tracking', actions: ['read', 'update'] },
      { module: 'supply-management', actions: ['read'] },
      { module: 'profile', actions: ['read', 'update'] },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];
